import { Slot } from "@radix-ui/react-slot";
import type { ComponentProps } from "react";

import { controleEmPilulaClass } from "@brevi/ui-kit/superficies";

import { cn } from "@/lib/utils";

// Barra de filtros das listagens (busca + selects + ações) calibrada para
// reproduzir 1:1 as regras-base `.process-toolbar` / `.toolbar-search` /
// `.toolbar-select` de styles.css. Os descendentes (`svg`, `input`, `select`)
// viram variantes arbitrárias (`[&_svg]:`, `[&_input]:`, `[&_select]:`), que
// ficam na layer `utilities` e por isso vencem o CSS legado. As utilitárias
// usam valores arbitrários onde o design foge da escala padrão, de propósito: a
// meta desta fase é preservar o visual enquanto o estilo migra do CSS global
// para o componente. Ver docs/design/ui-foundation.md.
//
// ⚠️ a utilitária de texto `muted` mapeia para `var(--surface-2)` (cor de FUNDO). Para texto
// esmaecido o token certo é `text-[var(--muted)]` — usado nos ícones.
//
// Os componentes ainda CARREGAM as classes legadas (`process-toolbar`,
// `toolbar-search`, `toolbar-select`) como 1º token. Isso mantém funcionando os
// overrides CONTEXTUAIS que dependem delas — hoje o
// `.process-toolbar .button.is-active` que vive unlayered em `tailwind.css`
// (chip de "pesquisa avançada"/"quadro travado" ativo). As classes saem quando
// esses seletores descendentes forem migrados.

type ToolbarProps = ComponentProps<"section"> & {
  asChild?: boolean;
};

export function Toolbar({ className, asChild = false, ...props }: ToolbarProps) {
  const Comp = asChild ? Slot : "section";
  return (
    <Comp
      className={cn(
        // TUT-469 — a barra de filtros é um BLOCO de vidro (16px), irmã do
        // painel de vidro logo abaixo dela.
        "process-toolbar flex gap-[0.65rem] mb-[0.85rem] p-[0.65rem] rounded-[var(--radius-bloco)] border border-[color:var(--vidro-borda)] bg-[color:var(--vidro)] shadow-[var(--vidro-sombra)] max-[720px]:flex-col",
        className,
      )}
      {...props}
    />
  );
}

// Campo de busca da barra. `asChild` cobre os usos que precisam de outro
// elemento: `<form>` (busca só ao dar Enter) e `<div>` (quando o `<label>` do
// controle é irmão, via `htmlFor`).
type ToolbarSearchProps = ComponentProps<"label"> & {
  asChild?: boolean;
};

export function ToolbarSearch({ className, asChild = false, ...props }: ToolbarSearchProps) {
  const Comp = asChild ? Slot : "label";
  return (
    <Comp
      className={cn(
        // TUT-282 — a altura mínima de 2,5 rem era um dos seis valores soltos;
        // agora lê do token padrão (--control-h), que por coincidência já era 2,5 rem.
        // TUT-510 — a busca da barra em PÍLULA sobre o vidro forte (Lumen).
        controleEmPilulaClass,
        "toolbar-search flex flex-1 items-center min-h-[var(--control-h)] px-[0.85rem] border text-corpo font-normal has-[:focus-visible]:border-focus has-[:focus-visible]:shadow-[0_0_0_3px_var(--focus-highlight)]",
        "[&_svg]:text-[var(--muted)]",
        // `self-stretch`, e não `h-full`: dentro de um flex `items-center` sem
        // altura DEFINIDA (só `min-height`), `height:100%` não resolve e o
        // input colapsa para a altura do texto — 18px de caixa para 14,7px de
        // letra, que é a "search box com texto maior que a caixa" que o dono
        // achou na Visão Geral em 03/08. Esticado, ele preenche os 2,5rem da
        // barra: o alvo de toque passa a ser a barra inteira (WCAG 2.5.8) e a
        // letra deixa de encostar nas bordas.
        // TUT-472 — `!outline-none`, e não `outline-none`: o `:focus-visible`
        // global do base.css mora FORA de layer e vence a utilitária. Sem o
        // `!`, o input focado ganhava o contorno deslocado POR CIMA da moldura
        // que a caixa pinta — duas marcas de foco, e a casa usa uma só. Só
        // fora do alto contraste: lá o navegador apaga o box-shadow e força a
        // cor da borda, e o contorno global é a única marca que sobra.
        "[&_input]:w-full [&_input]:self-stretch [&_input]:px-[0.65rem] [&_input]:py-0 [&_input]:border-0 [&_input]:not-forced-colors:!outline-none [&_input]:text-foreground [&_input]:bg-transparent",
        // Caixa estreita não pode serrar o placeholder no meio da palavra:
        // reticências dizem "tem mais", texto cortado diz "quebrou".
        "[&_input]:text-ellipsis [&_input::placeholder]:text-ellipsis",
        className,
      )}
      {...props}
    />
  );
}

// Select da barra: largura fixa de 220px no desktop, 100% no mobile.
type ToolbarSelectProps = ComponentProps<"label"> & {
  asChild?: boolean;
  /**
   * TUT-329 — `row` é o select que mora numa LINHA de tabela ao lado de ações
   * `size="row"` (o papel em Usuários): mesma altura compacta e mesma letra
   * delas. Na barra de filtros, o padrão.
   */
  size?: "default" | "row";
};

export function ToolbarSelect({ className, asChild = false, size = "default", ...props }: ToolbarSelectProps) {
  const Comp = asChild ? Slot : "label";
  return (
    <Comp
      data-size={size === "row" ? "row" : undefined}
      className={cn(
        // TUT-325 — `text-[0.86rem]` fixo: sem ele o select herdava a fonte de
        // onde morasse (numa célula de tabela, 0,73rem) e o mesmo controle
        // saía com dois tamanhos no app.
        // TUT-510 — o filtro da barra também em pílula, irmão da busca.
        controleEmPilulaClass,
        "toolbar-select flex w-[220px] min-w-0 items-center min-h-[var(--control-h)] pl-[0.85rem] border text-corpo font-normal max-[720px]:w-full has-[:focus-visible]:border-focus has-[:focus-visible]:shadow-[0_0_0_3px_var(--focus-highlight)]",
        size === "row" && "min-h-[var(--control-h-compact)] text-legenda",
        "[&_svg]:text-[var(--muted)]",
        // TUT-329 — a opção longa que vazava da caixa ("o nome maior que o
        // quadro", dono em 27/09) se resolve em `tailwind.css`, junto das
        // regras do select nativo: a seta passa para esta caixa e o select é
        // recortado na pintura. Utilitária não vence aquela regra global.
        // `!outline-none` pelo mesmo motivo do `ToolbarSearch`: a moldura de
        // foco é da caixa.
        "[&_select]:w-full [&_select]:min-w-0 [&_select]:h-full [&_select]:px-[0.65rem] [&_select]:py-0 [&_select]:border-0 [&_select]:not-forced-colors:!outline-none [&_select]:text-foreground [&_select]:bg-transparent [&_select]:cursor-pointer",
        className,
      )}
      {...props}
    />
  );
}

/**
 * TUT-479 (P2) — o filtro de ligar e desligar da barra ("Mostrar desativados").
 *
 * Era um `<label><input type="checkbox"> …</label>` escrito à mão em Assistentes,
 * Advogados, Assuntos e Locadoras: uma caixa nativa solta entre duas pílulas, sem
 * a altura, a borda nem o foco dos vizinhos. Aqui ele é a pílula da barra, irmã
 * da busca e do select (mesma receita, mesma altura, mesma letra), com a caixa
 * nativa dentro — o que mantém o teclado, o leitor de tela e o `name` que a e2e
 * procura. Ligado, a borda ganha o tom primário: o estado se lê sem olhar a caixa.
 *
 * A tela passa o `name`, o `checked`, o `onChange` e o rótulo; o desenho é daqui.
 */
type ToolbarToggleProps = Omit<ComponentProps<"input">, "type" | "className"> & {
  className?: string;
};

export function ToolbarToggle({ className, children, ...props }: ToolbarToggleProps) {
  return (
    <label
      data-slot="toolbar-toggle"
      className={cn(
        controleEmPilulaClass,
        "toolbar-toggle flex shrink-0 items-center gap-2 min-h-[var(--control-h)] px-[0.85rem] border text-corpo font-normal whitespace-nowrap text-[var(--muted)] cursor-pointer max-[720px]:w-full",
        // Ligado: a borda e a letra acompanham o estado.
        "has-[:checked]:border-primary has-[:checked]:text-foreground",
        "has-[:focus-visible]:border-focus has-[:focus-visible]:shadow-[0_0_0_3px_var(--focus-highlight)]",
        // A moldura de foco é da pílula (P20), como na busca e no select.
        "[&_input]:size-4 [&_input]:cursor-pointer [&_input]:accent-[var(--primary)] [&_input]:not-forced-colors:!outline-none",
        className,
      )}
    >
      <input type="checkbox" {...props} />
      {children}
    </label>
  );
}
